/**
 * useRequeueElement — mutation for "Bring back to queue" on an ELIMINATED
 * element. Invalidates the queue, the rotation history and the element's
 * timeline so every surface reflects the revive immediately.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { FlowLayer } from '@trafficpulse/shared';

import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';
import { requeueElement } from '../../../services/flow-optimizer-queue-api';
import { queueKeys } from '../../../hooks/useFlowOptimizerQueue';

export interface RequeueInput {
  layer: FlowLayer;
  bankId: string;
  /** Destination: the queue line (default) or the Waiting segment. */
  to?: 'queue' | 'waiting';
}

export function useRequeueElement(
  campaignId: string,
  provider: QueueProvider = 'voluum',
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RequeueInput) =>
      requeueElement(campaignId, input, provider),
    // RETURNED, not fire-and-forget: react-query waits for a promise returned
    // from onSuccess before settling mutateAsync. The write itself resolves in
    // milliseconds while the queue refetch takes seconds, so without this the
    // caller's dialog closed and the row sat there unchanged — the "it did
    // nothing" gap. Awaiting the list means the dialog closes once the row has
    // actually moved.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: queueKeys.queue(provider, campaignId),
        }),
        queryClient.invalidateQueries({
          queryKey: queueKeys.rotationHistory(provider, campaignId),
        }),
        // Prefix key — the element's timeline changed too.
        queryClient.invalidateQueries({
          queryKey: [provider, 'flow-optimizer', 'queue-events', campaignId],
        }),
      ]),
  });
}
